import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  input,
  output,
  signal,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, Validators, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';
import { AutocompleteOption, Opt, TagCfg } from './interfaces/filter-item.interface';
import { ChipComponent } from '../chip/chip.component';

@Component({
  selector: 'app-auto-complete',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ChipComponent
  ],
  templateUrl: './auto-complete-component.component.html',
  styleUrl: './auto-complete-component.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteComponent),
      multi: true,
    },
  ],
})
export class AutoCompleteComponent implements ControlValueAccessor {

  label = input<string | null>(null);
  placeholder = input<string>('Buscar…');
  options = input<Opt[]>([]);
  value = input<string | null>(null);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  errorMessage = input<string>('Este campo es obligatorio');
  messageEmpty = input<string>('Sin datos');
  searchByValueAndLabel = input<boolean>(false);

  valueChange = output<string | null>();

  // Estado interno
  readonly inputCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly term = signal<string>('');
  private readonly selected = signal<Opt | null>(null);
  private isClickingOption = signal<boolean>(false);
  private readonly writeInternalValue = signal<string | null>(null);
  private readonly writeInternalDisabled = signal<boolean>(false);


  // Helpers base
  getLabel = (o: Opt): string => (typeof o === 'string' ? o : o.label);
  getValue = (o: Opt): string => (typeof o === 'string' ? o : o.value);

  // Helpers jerarquía/tag
  isParent = (o: Opt): boolean => typeof o !== 'string' && o.isParent === true;
  getParentId = (o: Opt): string | null => (typeof o !== 'string' ? o.parentId ?? null : null);

  getTag(o: Opt): TagCfg | null {
    if (typeof o === 'string') return null;
    if (!o.tag) return null;
    return { text: o.tag, bgColor: o.bgColor, textColor: o.textColor };
  }

  readonly hasValue = computed(() => this.term().trim().length > 0);

  // Funciones callback de ControlValueAccessor
  private hasForm = signal<boolean>(false);
  private onFormChange: (value: any) => void = () => { };
  private onFormTouched: () => void = () => { };
  readonly matOptions = viewChildren(MatOption);

  /** Orden padre -> hijos -> huérfanos (igual que tu Select) */
  readonly displayOptions = computed<Opt[]>(() => {
    const opts = this.options() ?? [];

    const parents = opts.filter((o) => typeof o !== 'string' && o.isParent === true) as AutocompleteOption[];
    const children = opts.filter((o) => typeof o !== 'string' && o.isParent !== true) as AutocompleteOption[];
    const plainStrings = opts.filter((o) => typeof o === 'string') as string[];

    const childrenByParent = new Map<string, AutocompleteOption[]>();
    for (const c of children) {
      const pid = c.parentId;
      if (!pid) continue;
      const list = childrenByParent.get(pid) ?? [];
      list.push(c);
      childrenByParent.set(pid, list);
    }

    const out: Opt[] = [];

    // padre -> hijos
    for (const p of parents) {
      out.push({ ...p, isParent: true });
      const kids = childrenByParent.get(p.value) ?? [];
      for (const k of kids) out.push({ ...k, isParent: false });
    }

    // huérfanos: hijos sin parentId o parentId que no matchea un padre
    const parentValues = new Set(parents.map((p) => p.value));
    const orphans = children.filter((c) => !c.parentId || !parentValues.has(c.parentId));
    for (const o of orphans) out.push({ ...o, isParent: false });

    // strings (si tuvieras) al final
    for (const s of plainStrings) out.push(s);

    return out;
  });

  /** Filtrado preservando agrupación (no desordena) */
  readonly filteredOptions = computed(() => {
    const searchTerm = this.normalizeText(this.term());
    const opts = this.displayOptions();

    if (!searchTerm) return opts;

    const searchBoth = this.searchByValueAndLabel();
    // Primero identificamos qué opciones coinciden con el término
    const matches = opts.filter((o) => {
      const textToSearch = searchBoth
        ? `${this.getValue(o)} ${this.getLabel(o)}`
        : this.getLabel(o);
      return this.normalizeText(textToSearch).includes(searchTerm);
    });

    // Identificamos los parentIds de los hijos que coinciden
    const parentIdsWithMatchingChildren = new Set<string>();
    for (const o of matches) {
      const pid = this.getParentId(o);
      if (pid) parentIdsWithMatchingChildren.add(pid);
    }

    return opts.filter((o) => {
      // Si es un hijo y coincide, mostrarlo
      if (!this.isParent(o)) {
        return matches.includes(o);
      }

      // Si es un padre, solo mostrarlo si:
      // 1. El padre mismo coincide con el término Y tiene hijos que también coinciden, O
      // 2. Tiene hijos que coinciden (para mantener la jerarquía)
      const parentValue = this.getValue(o);
      const hasMatchingChildren = parentIdsWithMatchingChildren.has(parentValue);

      // Solo mostrar el padre si tiene hijos que coinciden
      return hasMatchingChildren;
    });
  });

  constructor() {
    effect(() => this.syncValidators(this.required()));

  
    effect(() => {
      const isDisabled = this.writeInternalDisabled();
      if (this.hasForm()) {
        this.syncDisabledState(isDisabled);
      }
    });

    effect(() => {
      const isDisabled = this.disabled();
       this.syncDisabledState(isDisabled);
    });


    effect(() => {
      const val = this.value();
      this.displayOptions();
      this.syncExternalValue(val);
    });

    effect(() => {
      const val = this.writeInternalValue();
      this.displayOptions();
      if (this.hasForm()) this.syncExternalValue(val);
    });
  }

  private syncValidators(isRequired: boolean): void {
    if (isRequired) this.inputCtrl.addValidators(Validators.required);
    else this.inputCtrl.removeValidators(Validators.required);
    this.inputCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private syncDisabledState(isDisabled: boolean): void {
    if (isDisabled && this.inputCtrl.enabled) this.inputCtrl.disable({ emitEvent: false });
    else if (!isDisabled && this.inputCtrl.disabled) this.inputCtrl.enable({ emitEvent: false });
  }

  private syncExternalValue(value: string | null): void {
    const foundOption = this.displayOptions().find((o) => this.getValue(o) === value) ?? null;
    this._selectOption(foundOption, { emit: false });
  }

  private _selectOption(opt: Opt | null, options: { emit: boolean } = { emit: true }): void {
    this.selected.set(opt);
    const label = opt ? this.getLabel(opt) : '';
    const value = opt ? this.getValue(opt) : null;

    if (this.inputCtrl.value !== label) {
      this.inputCtrl.setValue(label, { emitEvent: false });
    }
    this.term.set(label);
    // Si las opciones ya están en pantalla, marcamos la seleccionada
    if (opt) {
      setTimeout(() => {
        const target = this.matOptions().find(
          (m) => String(this.getValue(m.value)) === String(value)
        );
        if (target && !target.selected) {
          target.select();
        }
      });
    }
    if (options.emit) {

      this.onFormChange(value);
      this.valueChange.emit(value);
       this.writeInternalValue.set(value);
    }
  }

  onTyping(event: Event): void {
    const text = (event.target as HTMLInputElement).value;

    if (this.selected() !== null) {
      this.selected.set(null);
      this.valueChange.emit(null);
      this.writeInternalValue.set(null);
      this.onFormChange(null);
    }

    this.term.set(text);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this._selectOption(event.option.value as Opt);
    this.isClickingOption.set(false);
  }

  onBlur(): void {
    this.onFormTouched();

    setTimeout(() => {
      if (this.isClickingOption()) return;

      const currentSelection = this.selected();
      if (!currentSelection) {
        if (this.inputCtrl.value !== '') this._selectOption(null);
      } else {
        const correctLabel = this.getLabel(currentSelection);
        if (this.inputCtrl.value !== correctLabel) {
          this.inputCtrl.setValue(correctLabel, { emitEvent: false });
          this.term.set(correctLabel);
        }
      }
    }, 150);
  }

  onOptionMouseDown(): void {
    this.isClickingOption.set(true);
  }

  onPanelClosed(): void {
    this.isClickingOption.set(false);
  }

  private normalizeText(text: string | null | undefined): string {
  if (!text) return '';
  return (
    text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      // Borra todas las tildes y acentos EXCEPTO la ñ (\u0303)
      .replace(/[\u0300-\u0302\u0304-\u036f]/g, '')
      .normalize('NFC') // Vuelve a recomponer la 'ñ'
      .replace(/\s+/g, ' ')
      .trim()
  );
}

  // ================= ControlValueAccessor Métodos ================= //
  writeValue(val: any): void {
    let safeVal: string | null = null;
    if (typeof val === 'string') {
      safeVal = val;
    } else if (typeof val === 'number' && Number.isInteger(val)) {
      safeVal = String(val); 
    }
    this.hasForm.set(true);
    this.writeInternalValue.set(safeVal);
  }
  registerOnChange(fn: any): void {
    this.onFormChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onFormTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.hasForm.set(true);
    this.writeInternalDisabled.set(isDisabled);
  }

  // =============================================================== //

  onPanelOpened(): void {
    const current = this.selected();
    if (!current) return;
    const currentVal = String(this.getValue(current));

    // Leemos la Signal this.matOptions()
    const target = this.matOptions().find(
      (m) => String(this.getValue(m.value)) === currentVal
    );
    if (target && !target.selected) {
      target.select();
    }
  }
}